import dayjs from 'dayjs';
import Cookies, { type CookieAttributes } from 'js-cookie';

export const COOKIE_KEYS = {
    TOKEN_VARIABLE: '@session_token',
    BRANCH_ID: '@branch_id',
    REGISTER_AUTH_TOKEN: '@register_auth_token',
    DEVICE_INFO_VARIABLE: 'user-device-information',
} as const;

export const COOKIES_TO_CLEAR = [
    COOKIE_KEYS.TOKEN_VARIABLE,
    COOKIE_KEYS.REGISTER_AUTH_TOKEN,
    COOKIE_KEYS.BRANCH_ID,
];

const TOKEN_BACKUP_KEY = 'moniebook_backup_token';
const REGISTER_AUTH_TOKEN_BACKUP_KEY = 'moniebook_register_auth_token_backup';

function isJsonString(str: string) {
    try {
        JSON.parse(str);
    } catch {
        return false;
    }

    return true;
}

const defaultOptions: CookieAttributes = {
    path: '/',
    expires: dayjs().add(1, 'year').toDate(),
};

export function setCookie(
    key: string,
    data: unknown,
    options?: CookieAttributes,
) {
    const allOptions = { ...defaultOptions, ...options };

    if (typeof data === 'string') {
        Cookies.set(key, data, allOptions);
        return;
    }

    const stringifiedData = JSON.stringify(data || {});
    Cookies.set(key, stringifiedData, allOptions);
}

export const getCookie = (key: string) => {
    const storeData = Cookies.get(key);
    const isValid = isJsonString(storeData ?? '');

    if (isValid) {
        return JSON.parse(storeData ?? '{}');
    }

    if (!storeData) {
        return undefined;
    }

    return storeData;
};

export const removeCookie = (key: string) =>
    Cookies.remove(key, defaultOptions);

export const removeAllCookies = () => {
    COOKIES_TO_CLEAR.forEach((cookie) => removeCookie(cookie));
    try {
        localStorage.removeItem(TOKEN_BACKUP_KEY);
        localStorage.removeItem(REGISTER_AUTH_TOKEN_BACKUP_KEY);
    } catch {
        // Silently fail
    }
};

export function setTokenVariable(token: string): void {
    setCookie(COOKIE_KEYS.TOKEN_VARIABLE, token);
    try {
        localStorage.setItem(TOKEN_BACKUP_KEY, token);
    } catch {
        // Silently fail if localStorage is unavailable
    }
}

export function getTokenVariable(): string | undefined {
    const token = getCookie(COOKIE_KEYS.TOKEN_VARIABLE);

    if (token) {
        try {
            if (!localStorage.getItem(TOKEN_BACKUP_KEY)) {
                localStorage.setItem(TOKEN_BACKUP_KEY, token);
            }
        } catch {
            // Silently fail
        }
        return token;
    }

    try {
        const backup = localStorage.getItem(TOKEN_BACKUP_KEY);
        if (backup) {
            setCookie(COOKIE_KEYS.TOKEN_VARIABLE, backup);
            return backup;
        }
    } catch {
        // Silently fail
    }

    return undefined;
}

export function clearTokenVariable(): void {
    removeCookie(COOKIE_KEYS.TOKEN_VARIABLE);
    try {
        localStorage.removeItem(TOKEN_BACKUP_KEY);
    } catch {
        // Silently fail
    }
}

// Register auth token management (V2 flow)
export function setRegisterAuthToken(token: string): void {
    setCookie(COOKIE_KEYS.REGISTER_AUTH_TOKEN, token);
    try {
        localStorage.setItem(REGISTER_AUTH_TOKEN_BACKUP_KEY, token);
    } catch {
        // Silently fail if localStorage is unavailable
    }
}

export function getRegisterAuthToken(): string | undefined {
    const token = getCookie(COOKIE_KEYS.REGISTER_AUTH_TOKEN);

    if (token) {
        try {
            if (!localStorage.getItem(REGISTER_AUTH_TOKEN_BACKUP_KEY)) {
                localStorage.setItem(REGISTER_AUTH_TOKEN_BACKUP_KEY, token);
            }
        } catch {
            // Silently fail
        }
        return token;
    }

    try {
        const backup = localStorage.getItem(REGISTER_AUTH_TOKEN_BACKUP_KEY);
        if (backup) {
            setCookie(COOKIE_KEYS.REGISTER_AUTH_TOKEN, backup);
            return backup;
        }
    } catch {
        // Silently fail
    }

    return undefined;
}

export function clearRegisterAuthToken(): void {
    removeCookie(COOKIE_KEYS.REGISTER_AUTH_TOKEN);
    try {
        localStorage.removeItem(REGISTER_AUTH_TOKEN_BACKUP_KEY);
    } catch {
        // Silently fail
    }
}

export function setBranchId(id: string): void {
    setCookie(COOKIE_KEYS.BRANCH_ID, id);
}

export function getBranchId(): string | undefined {
    return getCookie(COOKIE_KEYS.BRANCH_ID);
}

export function clearBranchId(): void {
    removeCookie(COOKIE_KEYS.BRANCH_ID);
}

// Aliases for clarity (V2 flow)
export const setBranchAuthToken = setTokenVariable;
export const clearBranchAuthToken = clearTokenVariable;
